// =============================================================================
// OBIEG DOKUMENTÓW — KARTOTEKA STANÓW DOKUMENTU (UD-12a, plan 53 §11.4)
//
// Jedno miejsce, w którym stan dokumentu z rejestru (`commerce_documents`,
// migracja 786) dostaje kod, etykietę, zdanie dla człowieka i ton. Czyta ją
// dziś ekran „Organizacja › Obieg dokumentów” w powłoce; w UD-12 dojdą mail,
// Nexus i API dla systemu sprzedaży — dlatego kartoteka mieszka w
// `@insolitum/types`, od którego te procesy zależą, a nie w powłoce.
//
// TRZY RZECZY, KTÓRE TEN PLIK PILNUJE:
//
//  * KAŻDY KOD MA PRODUCENTA I CZYTELNIKA. Kody planu, których dziś nic nie
//    wytwarza (oś odbiorcy z UD-8b, `potwierdzenie_nieudane` z martwej gałęzi
//    `blad`), tu NIE wchodzą — wejdą razem ze swoim producentem. Kod bez
//    producenta byłby obietnicą stanu, którego ekran nigdy nie pokaże.
//  * NIEZNANE NIE UDAJE ZNANEGO. Kod spoza kartoteki daje `null`, a ekran
//    pokazuje „Nie podaje” z kodem. `nie_podaje` ma własny, neutralny ton
//    `nieznane` i glif „?” — nie bursztyn, bo bursztyn znaczy „coś się dzieje”,
//    a tu nie wiemy nic.
//  * ZDANIA NIE OBIECUJĄ TEGO, CZEGO NIE MA. Żadne nie twierdzi, że dokument
//    trafił do systemu księgowego (Universe sam go tam nie wysyła), i żadne nie
//    nazywa systemu księgowego — kartoteka jest wspólna dla wszystkich firm.
//    Wyjątek nazwany (MB-1a, plan 53 §11.4 v3.4): `bez_odbiorcy` mówi WARUNKOWO,
//    gdzie szukać stanu wysyłki, gdy firma wysyła dokumenty przez Mandatum —
//    to organ platformy, nie system księgowy klienta.
//
// Plik NIE IMPORTUJE NICZEGO. Wygląd (klasy plakietek) liczy powłoka.
// Liczby w zdaniach (6 godzin, 12 godzin, 35 dni, 30 minut) mają pokrycie
// w stałych i w migracji 786 — pilnuje tego
// `tests/unit/apps/kontraktListyDokumentow.test.ts`.
// =============================================================================

/** Ton plakietki — sześć wartości, każda z własną klasą w powłoce. */
export const TONY_OBIEGU = ['pending', 'muted', 'active', 'trial', 'error', 'nieznane'] as const;
export type TonObiegu = (typeof TONY_OBIEGU)[number];

/** Jedyna czynność UD-12a: ponowny odczyt listy (plan 53 §11.4). */
export type CzynnoscObiegu = 'odswiez';

/** Oś stanu — kolejność osi jest kolejnością objaśnienia na ekranie. */
export const OSIE_OBIEGU = ['przekazanie', 'potwierdzenie', 'kontrola', 'wspolny'] as const;
export type OsObiegu = (typeof OSIE_OBIEGU)[number];

export interface OpisStanuObiegu {
  os: OsObiegu;
  etykieta: string;
  zdanie: string;
  ton: TonObiegu;
  glif: '?' | null;
  czynnosc: CzynnoscObiegu | null;
  /**
   * Liczba mnoga do liczników karty agenta (`LICZNIKI_OBIEGU` w powłoce), np.
   * „zmienione po przekazaniu” — karta i lista mówią o stanie tym samym słowem.
   */
  licznik?: string;
}

/** Kody stanu dokumentu w zakresie UD-12a — w kolejności objaśnienia (oś po osi). */
export const KODY_STANU_DOKUMENTU = [
  'zmieniony_w_zrodle',
  'usuniety_w_zrodle',
  'bez_odbiorcy',
  'nie_dotyczy',
  'czeka_na_potwierdzenie',
  'potwierdzenie_opoznione',
  'potwierdzony',
  'odmowa',
  'kontrola_biezaca',
  'kontrola_nie_nadaza',
  'kontrola_poza_oknem',
  'nie_podaje',
] as const;

export type KodStanuDokumentu = (typeof KODY_STANU_DOKUMENTU)[number];

export const STANY_DOKUMENTU: Record<KodStanuDokumentu, OpisStanuObiegu> = {
  zmieniony_w_zrodle: {
    os: 'przekazanie',
    etykieta: 'Zmieniony po przekazaniu',
    zdanie:
      'Ktoś zmienił ten dokument w Receptorze po tym, jak trafił do Universe. Universe trzyma wersję sprzed zmiany i niczego nie nadpisuje. Sprawdź w Receptorze, kto i dlaczego go zmienił.',
    ton: 'error',
    glif: null,
    czynnosc: null,
    licznik: 'zmienione po przekazaniu',
  },
  usuniety_w_zrodle: {
    os: 'przekazanie',
    etykieta: 'Usunięty po przekazaniu',
    zdanie:
      'Tego dokumentu nie ma już w Receptorze, choć trafił do Universe. Universe zachowuje go bez zmian. Sprawdź w Receptorze, kto i dlaczego go usunął.',
    ton: 'error',
    glif: null,
    czynnosc: null,
    licznik: 'usunięte po przekazaniu',
  },
  bez_odbiorcy: {
    os: 'przekazanie',
    etykieta: 'W Universe',
    zdanie:
      'Dokument jest zapisany w Universe. Universe sam nie przekazuje go do systemu księgowego. Jeśli firma wysyła dokumenty z Receptora przez Mandatum, stan wysyłki jest w Mandatum na ekranie Dokumenty › Z Receptora.',
    ton: 'muted',
    glif: null,
    czynnosc: null,
  },
  nie_dotyczy: {
    os: 'przekazanie',
    etykieta: 'Nie dotyczy',
    zdanie:
      'Ten dokument nie idzie do systemu księgowego: to zapis wydania w Receptorze, a dokument wydania wystawia inny system.',
    ton: 'muted',
    glif: null,
    czynnosc: null,
  },
  czeka_na_potwierdzenie: {
    os: 'potwierdzenie',
    etykieta: 'Potwierdzanie w toku',
    zdanie:
      'Universe zapisał dokument i odsyła Receptorowi potwierdzenie. Robi to sam, w ciągu kilku minut.',
    ton: 'pending',
    glif: null,
    czynnosc: null,
  },
  potwierdzenie_opoznione: {
    os: 'potwierdzenie',
    etykieta: 'Potwierdzenie się opóźnia',
    zdanie:
      'Receptor nie potwierdził jeszcze tego dokumentu, choć od zapisania w Universe minęło ponad 30 minut. Sprawdź stan odczytu nad listą; jeśli działa, a stan się utrzymuje, zgłoś to opiekunowi.',
    ton: 'trial',
    glif: null,
    czynnosc: null,
  },
  potwierdzony: {
    os: 'potwierdzenie',
    etykieta: 'Potwierdzony w Receptorze',
    zdanie: 'Receptor potwierdził, że wie o zapisaniu dokumentu w Universe.',
    ton: 'active',
    glif: null,
    czynnosc: null,
    licznik: 'potwierdzone w Receptorze',
  },
  odmowa: {
    os: 'potwierdzenie',
    etykieta: 'Receptor odmówił',
    zdanie:
      'Receptor nie przyjął potwierdzenia tego dokumentu. Sprawdź dokument w Receptorze; jeśli wygląda poprawnie, zgłoś to opiekunowi.',
    ton: 'error',
    glif: null,
    czynnosc: null,
    licznik: 'odmowy Receptora',
  },
  kontrola_biezaca: {
    os: 'kontrola',
    etykieta: 'Sprawdzony',
    zdanie: 'Universe sprawdza co 6 godzin, czy dokument w Receptorze się nie zmienił.',
    ton: 'muted',
    glif: null,
    czynnosc: null,
  },
  kontrola_nie_nadaza: {
    os: 'kontrola',
    etykieta: 'Kontrola nie nadąża',
    zdanie:
      'Ten dokument był ostatnio sprawdzony dawniej niż 12 godzin temu. Kontrola nadrabia sama; jeśli stan się utrzymuje, zgłoś to opiekunowi.',
    ton: 'trial',
    glif: null,
    czynnosc: null,
  },
  kontrola_poza_oknem: {
    os: 'kontrola',
    etykieta: 'Poza kontrolą',
    zdanie:
      'Universe sprawdza dokumenty potwierdzone w ostatnich 35 dniach. Ten jest starszy, więc nie jest już sprawdzany.',
    ton: 'muted',
    glif: null,
    czynnosc: null,
  },
  nie_podaje: {
    os: 'wspolny',
    etykieta: 'Nie podaje',
    zdanie:
      'Nie wiemy, co się stało z tym dokumentem: Receptor nie odpowiedział albo podał stan, którego ta wersja panelu nie zna.',
    ton: 'nieznane',
    glif: '?',
    czynnosc: 'odswiez',
  },
};

const jestKodemStanu = (kod: string): kod is KodStanuDokumentu =>
  (KODY_STANU_DOKUMENTU as readonly string[]).includes(kod);

/**
 * Opis kodu albo `null` dla kodu spoza kartoteki (wzór `opisWerdyktuZapasu`) —
 * ekran pokazuje wtedy „Nie podaje” z kodem, a nie najbliższy znany stan.
 */
export function opisStanuDokumentu(kod: string): OpisStanuObiegu | null {
  return jestKodemStanu(kod) ? STANY_DOKUMENTU[kod] : null;
}

// ── Zbiory z CHECK-ów migracji 786 ──────────────────────────────────────────
//
// KOPIE wartości więzów `commerce_documents_*_chk` — zgodność co do znaku
// pilnuje bramka z korzenia, która czyta plik migracji.

/** `commerce_documents_rodzaj_magazynowy_chk`. */
export const RODZAJE_DOKUMENTU_MAGAZYNOWEGO = ['pz', 'pw', 'rw', 'mm', 'inw', 'wz'] as const;
export type RodzajDokumentuMagazynowego = (typeof RODZAJE_DOKUMENTU_MAGAZYNOWEGO)[number];

/** `commerce_documents_cel_chk`. */
export const CELE_DOKUMENTU = [
  'przyjecie_dostawy',
  'przyjecie_z_produkcji',
  'rozchod_produkcyjny',
  'rozchod_reczny',
  'przesuniecie',
  'przesuniecie_kwarantanna',
  'inwentaryzacja',
  'zwrot_do_dostawcy',
  'wydanie_handlowe',
  'wydanie_zewnetrzne',
  'korekta',
] as const;
export type CelDokumentu = (typeof CELE_DOKUMENTU)[number];

/** `commerce_documents_stan_zrodla_chk`. */
export const STANY_ZRODLA = ['zarejestrowany', 'zmieniony_w_zrodle', 'usuniety_w_zrodle'] as const;
export type StanZrodla = (typeof STANY_ZRODLA)[number];

/** `commerce_documents_potwierdzenie_chk` — plus NULL („jeszcze nie potwierdzono”). */
export const POTWIERDZENIA_WYSTAWCY = ['potwierdzony', 'odmowa'] as const;
export type PotwierdzenieWystawcy = (typeof POTWIERDZENIA_WYSTAWCY)[number];

// ── Słowniki ────────────────────────────────────────────────────────────────

/** Nazwy rodzajów (plan 53 §18), uogólnione tam, gdzie rodzaj obejmuje kilka celów. */
export const NAZWY_RODZAJOW: Record<RodzajDokumentuMagazynowego, { skrot: string; nazwa: string }> =
  {
    pz: { skrot: 'PZ', nazwa: 'przyjęcie zewnętrzne (dostawa)' },
    pw: { skrot: 'PW', nazwa: 'przyjęcie wewnętrzne (wyrób z produkcji)' },
    rw: { skrot: 'RW', nazwa: 'rozchód wewnętrzny (zużycie surowca)' },
    mm: { skrot: 'MM', nazwa: 'przesunięcie międzymagazynowe' },
    inw: { skrot: 'INW', nazwa: 'inwentaryzacja' },
    wz: { skrot: 'WZ', nazwa: 'wydanie zewnętrzne' },
  };

/** Etykiety celów dokumentu (plan 53 §18, tabela „Cele dokumentu”). */
export const NAZWY_CELOW: Record<CelDokumentu, string> = {
  przyjecie_dostawy: 'przyjęcie dostawy',
  przyjecie_z_produkcji: 'przyjęcie z produkcji',
  rozchod_produkcyjny: 'zużycie w produkcji',
  rozchod_reczny: 'pobranie ręczne (np. z silosu)',
  przesuniecie: 'przesunięcie',
  przesuniecie_kwarantanna: 'przesunięcie z kontroli przyjęcia (kwarantanna)',
  inwentaryzacja: 'inwentaryzacja',
  zwrot_do_dostawcy: 'zwrot do dostawcy',
  wydanie_handlowe: 'wydanie dla klienta',
  wydanie_zewnetrzne: 'wydanie zewnętrzne (zapis stanu w Receptorze)',
  korekta: 'korekta',
};

/** Cele, które z założenia nie idą do systemu księgowego (plan 53 §4.2). */
export const CELE_POZA_KSIEGOWOSCIA = ['wydanie_zewnetrzne'] as const;

/** Stan nazwany: pole nie ma tu zastosowania (np. dzień produkcji przy dostawie). */
export const NIE_DOTYCZY = 'nie dotyczy';
/** Stan nazwany: pole powinno być, a źródło go nie podało. */
export const NIE_PODAJE = 'nie podaje';
